import { useState } from 'react'
import { SectionLabel } from '../ui/SectionLabel'

const additionalCosts = [
  { item: 'VPN', cost: 10 },
  { item: 'Data Backup', cost: 10 },
  { item: 'Antivirus', cost: 7.50 },
  { item: 'SOC 24/7 Monitoring', cost: 10 },
  { item: 'Password Manager', cost: 3 },
  { item: 'Email & Cloud App Backup', cost: 28 },
  { item: 'Device Management (MDM)', cost: 8.50 },
  { item: '16/7 Help Desk Support', cost: 49 },
  { item: 'Project Costs (installs, upgrades)', cost: 30 },
  { item: 'Cybersecurity Training', cost: 9 },
]

const NTC_PRICE = 219

const LOCALE = 'en-US'

function fmt(n: number) {
  return '$' + Math.round(n).toLocaleString(LOCALE)
}

function fmtDec(n: number) {
  return '$' + n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

interface StatCardProps {
  label: string
  internal: string
  ntc: string
  highlight?: boolean
}

function StatCard({ label, internal, ntc, highlight = false }: StatCardProps) {
  return (
    <div className={`grid grid-cols-3 gap-px ${highlight ? 'rounded-lg overflow-hidden' : ''}`}
      style={highlight ? { background: 'var(--accent-glow)', border: '1px solid var(--accent-line)', borderRadius: 8 } : {}}>
      <div className="px-4 py-3 text-sm"
        style={{ color: 'var(--white-dim)', ...(highlight ? {} : { background: 'var(--bg-3)' }) }}>
        {label}
      </div>
      <div className={`px-4 py-3 text-sm text-right ${highlight ? 'font-semibold' : ''}`}
        style={{ color: highlight ? 'var(--white)' : 'var(--white-dim)', ...(highlight ? {} : { background: 'var(--bg-3)' }) }} suppressHydrationWarning>
        {internal}
      </div>
      <div className="px-4 py-3 text-sm text-right font-bold"
        style={{ color: highlight ? 'var(--accent)' : 'var(--white)', ...(highlight ? {} : { background: 'var(--bg-3)' }) }} suppressHydrationWarning>
        {ntc}
      </div>
    </div>
  )
}

interface SliderInputProps {
  id: string
  label: string
  value: number
  min: number
  max: number
  step?: number
  onChange: (v: number) => void
  prefix?: string
  suffix?: string
}

function SliderInput({ id, label, value, min, max, step = 1, onChange, prefix = '', suffix = '' }: SliderInputProps) {
  const valueStr = `${prefix}${value.toLocaleString(LOCALE)}${suffix}`
  const minStr = `${prefix}${min.toLocaleString(LOCALE)}${suffix}`
  const maxStr = `${prefix}${max.toLocaleString(LOCALE)}${suffix}`
  return (
    <div className="mb-6">
      <label htmlFor={id} className="sr-only">
        {label}
      </label>
      <div className="flex justify-between mb-2">
        <span className="text-sm" style={{ color: 'var(--white-dim)', fontFamily: 'Barlow, sans-serif' }}>{label}</span>
        <span className="text-sm font-bold" style={{ color: 'var(--accent)' }} suppressHydrationWarning>
          {valueStr}
        </span>
      </div>
      <input
        id={id}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={e => onChange(Number(e.target.value))}
        className="w-full cursor-pointer"
        style={{ accentColor: 'var(--accent)', height: 4 }}
        aria-label={label}
      />
      <div className="flex justify-between mt-1">
        <span className="text-xs" style={{ color: 'var(--white-faint)' }} suppressHydrationWarning>{minStr}</span>
        <span className="text-xs" style={{ color: 'var(--white-faint)' }} suppressHydrationWarning>{maxStr}</span>
      </div>
    </div>
  )
}

export function Calculator() {
  const [tab, setTab] = useState<'it' | 'ai'>('it')
  const [employees, setEmployees] = useState(20)
  const [showBreakdown, setShowBreakdown] = useState(false)

  // AI ROI
  const [teamSize, setTeamSize] = useState(10)
  const [weeklyHours, setWeeklyHours] = useState(8)
  const [hourlyRate, setHourlyRate] = useState(50)

  // IT Cost calculations
  const internalSalary = employees <= 5 ? 50000 : 50000 + (employees - 5) * 2750
  const internalMonthly = internalSalary / 12
  const internalPerEmployee = internalMonthly / employees
  const additionalTotal = additionalCosts.reduce((sum, c) => sum + c.cost, 0)
  const internalTotalPerEmployee = internalPerEmployee + additionalTotal
  const internalTotalMonthly = internalMonthly + additionalTotal * employees
  const internalTotalYearly = internalTotalMonthly * 12
  const ntcMonthly = employees * NTC_PRICE
  const ntcYearly = ntcMonthly * 12
  const savings = internalTotalYearly - ntcYearly

  // AI ROI calculations
  const annualCostRepetitive = teamSize * weeklyHours * hourlyRate * 52
  const hoursSaved = teamSize * weeklyHours * 0.6 * 52
  const annualValueRecovered = hoursSaved * hourlyRate

  return (
    <section id="calculator" style={{ background: 'var(--bg-2)', padding: '120px 0' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 24px' }}>
        {/* Eyebrow */}
        <SectionLabel className="mb-6">Run The Numbers</SectionLabel>
        {/* Headline */}
        <h2
          style={{
            fontFamily: 'Barlow Condensed, sans-serif',
            fontWeight: 900,
            fontSize: 'clamp(38px, 4.5vw, 58px)',
            textTransform: 'uppercase',
            lineHeight: 1.0,
            marginBottom: 20,
            color: 'var(--white)',
          }}
        >
          See What You're <span style={{ color: 'var(--accent)' }}>Actually Spending.</span>
        </h2>
        <p
          style={{
            fontSize: 15,
            lineHeight: 1.75,
            color: 'var(--white-dim)',
            marginBottom: 36,
            textAlign: 'center',
          }}
        >
          Most businesses don't realize how much fragmented IT support truly costs. See the real comparison — and calculate what AI automation could save your team every year.
        </p>
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <a
            href="/contact"
            className="btn-primary inline-flex items-center justify-center no-underline"
            style={{ textDecoration: 'none' }}
          >
            Book a Free Cost Audit →
          </a>
        </div>

        {/* Calculator box */}
        <div
          className="calculator-box relative overflow-hidden"
          style={{
            background: 'var(--bg-3)',
            border: '1px solid var(--accent-line)',
            padding: 48,
            borderRadius: 10,
          }}
        >
          {/* Tabs — filled pill inside bordered container */}
          <div
            style={{
              display: 'flex',
              gap: 4,
              marginBottom: 40,
              background: 'var(--bg-2)',
              padding: 4,
              borderRadius: 10,
              border: '1px solid var(--accent-line)',
            }}
          >
            {(['it', 'ai'] as const).map(t => (
              <button
                key={t}
                onClick={() => setTab(t)}
                style={{
                  flex: 1,
                  padding: '12px 24px',
                  borderRadius: 7,
                  border: 'none',
                  cursor: 'pointer',
                  fontFamily: 'Barlow Condensed, sans-serif',
                  fontWeight: 700,
                  fontSize: 15,
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                  background: tab === t ? 'var(--accent)' : 'transparent',
                  color: tab === t ? '#fff' : 'var(--white-dim)',
                  boxShadow: tab === t ? '0 4px 20px var(--accent-glow)' : 'none',
                }}
              >
                {t === 'it' ? 'IT Cost Calculator' : 'AI ROI Calculator'}
              </button>
            ))}
          </div>

        {/* IT Cost Tab — full width, two-column internally */}
        {tab === 'it' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

            {/* Input card */}
            <div className="rounded-xl p-8" style={{
              background: 'var(--bg-2)',
              border: '1px solid var(--accent-line)',
            }}>
              <h3 className="uppercase font-bold mb-7 tracking-wide" style={{
                fontFamily: 'Barlow Condensed, sans-serif',
                fontWeight: 700,
                color: 'var(--white)',
              }}>YOUR BUSINESS</h3>

              <SliderInput
                id="employees-slider"
                label="Number of employees"
                value={employees}
                min={1}
                max={200}
                onChange={setEmployees}
                suffix=" people"
              />

              {/* Stepper row — inline − / numeric / + */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '8px 0 20px' }}>
                <button
                  onClick={() => setEmployees(Math.max(1, employees - 1))}
                  style={{
                    width: 32, height: 32, borderRadius: 4,
                    background: 'var(--bg-4)', border: '1px solid var(--accent-line)',
                    color: 'var(--white)', cursor: 'pointer', fontSize: 18, lineHeight: 1,
                  }}
                >−</button>
                <span style={{
                  minWidth: 48, textAlign: 'center',
                  fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 700,
                  fontSize: 22, color: 'var(--white)',
                }} suppressHydrationWarning>{employees}</span>
                <button
                  onClick={() => setEmployees(Math.min(200, employees + 1))}
                  style={{
                    width: 32, height: 32, borderRadius: 4,
                    background: 'var(--bg-4)', border: '1px solid var(--accent-line)',
                    color: 'var(--white)', cursor: 'pointer', fontSize: 18, lineHeight: 1,
                  }}
                >+</button>
              </div>

              {/* Per-employee costs included + Show Breakdown button */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <span style={{ color: 'var(--white-dim)', fontSize: 13 }}>Per-employee costs included</span>
                <button
                  onClick={() => setShowBreakdown(!showBreakdown)}
                  style={{
                    background: 'transparent',
                    border: '1px solid var(--accent-line)',
                    borderRadius: 4,
                    color: 'var(--accent)',
                    fontSize: 12,
                    fontFamily: 'Barlow Condensed, sans-serif',
                    fontWeight: 700,
                    padding: '4px 10px',
                    cursor: 'pointer',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >{showBreakdown ? 'Hide Breakdown' : 'Show Breakdown'}</button>
              </div>

              {showBreakdown && (
                <div className="rounded-lg p-4 mb-3" style={{ background: 'var(--bg-3)' }}>
                  {additionalCosts.map((c, i) => (
                    <div key={i} className="flex justify-between py-1.5 text-xs"
                      style={{ borderBottom: i < additionalCosts.length - 1 ? '1px solid rgba(238,240,245,0.07)' : 'none' }}>
                      <span style={{ color: 'var(--white-dim)' }}>{c.item}</span>
                      <div className="flex gap-4">
                        <span style={{ color: 'var(--white-dim)' }}>{fmtDec(c.cost)}</span>
                        <span className="font-semibold" style={{ color: 'var(--accent)' }}>Included</span>
                      </div>
                    </div>
                  ))}
                  <div className="flex justify-between pt-2.5 mt-1 text-sm font-semibold">
                    <span style={{ color: 'var(--white)' }}>Total per employee/mo</span>
                    <div className="flex gap-4">
                      <span style={{ color: 'var(--white)' }}>{fmtDec(additionalTotal)}</span>
                      <span style={{ color: 'var(--accent)' }}>$0.00</span>
                    </div>
                  </div>
                </div>
              )}

              <div className="rounded-lg p-4" style={{
                background: 'var(--accent-glow)',
                border: '1px solid var(--accent-line)',
              }}>
                <span className="text-sm" style={{ color: 'var(--white-dim)' }}>NTC flat rate: </span>
                <span className="text-sm font-bold" style={{ color: 'var(--accent)' }} suppressHydrationWarning>${NTC_PRICE}/seat/month</span>
                <span className="text-sm" style={{ color: 'var(--white-dim)' }}> — everything included</span>
              </div>
            </div>

            {/* Results */}
            <div>
              {savings > 0 && (
                <div className="rounded-xl p-6 mb-6 text-center" style={{
                  background: 'var(--accent-glow)',
                  border: '1px solid var(--accent)',
                }}>
                  <div className="text-sm mb-1" style={{ color: 'var(--white-dim)' }}>
                    Estimated annual savings with NTC
                  </div>
                  <div className="font-black leading-none mb-1" style={{
                    fontFamily: 'Barlow Condensed, sans-serif',
                    fontSize: 48,
                    color: 'var(--accent)',
                  }} suppressHydrationWarning>{fmt(savings)}</div>
                  <div className="text-xs" style={{ color: 'var(--white-dim)' }}>per year</div>
                </div>
              )}

              {/* Column headers */}
              <div className="grid grid-cols-3 gap-px mb-0.5">
                <div className="px-4 py-2" />
                <div className="px-4 py-2 text-right text-xs font-bold uppercase tracking-wider"
                  style={{ color: 'var(--white-dim)', fontFamily: 'Barlow Condensed, sans-serif' }}>
                  Hire Internally
                </div>
                <div className="px-4 py-2 text-right text-xs font-bold uppercase tracking-wider"
                  style={{ color: 'var(--accent)', fontFamily: 'Barlow Condensed, sans-serif' }}>
                  NTC
                </div>
              </div>

              <div className="flex flex-col" style={{ gap: 2 }}>
                <StatCard label="Per employee / month" internal={fmtDec(internalPerEmployee)} ntc={fmtDec(NTC_PRICE)} />
                <StatCard label="+ Additional costs / employee" internal={fmtDec(additionalTotal)} ntc="$0.00" />
                <StatCard label="Total per employee / month" internal={fmtDec(internalTotalPerEmployee)} ntc={fmtDec(NTC_PRICE)} highlight />
                <div className="h-3" />
                <StatCard label="Monthly total" internal={fmt(internalTotalMonthly)} ntc={fmt(ntcMonthly)} />
                <StatCard label="Annual total" internal={fmt(internalTotalYearly)} ntc={fmt(ntcYearly)} highlight />
              </div>

              <div className="text-center mt-6">
                <a href="/contact" className="btn-primary inline-flex items-center justify-center no-underline" style={{ textDecoration: 'none' }}>
                  Book a Free Cost Audit →
                </a>
              </div>
            </div>
          </div>
        )}

        {/* AI ROI Tab */}
        {tab === 'ai' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

            {/* Inputs */}
            <div className="rounded-xl p-8" style={{
              background: 'var(--bg-2)',
              border: '1px solid var(--accent-line)',
            }}>
              <h3 className="uppercase font-bold mb-7 tracking-wide text-lg" style={{
                fontFamily: 'Barlow Condensed, sans-serif',
                color: 'var(--white)',
              }}>Your Team</h3>

              <SliderInput id="team-size-slider" label="Team size" value={teamSize} min={2} max={100} onChange={setTeamSize} suffix=" people" />
              <SliderInput id="weekly-hours-slider" label="Hours per week on repetitive tasks (per person)" value={weeklyHours} min={1} max={30} onChange={setWeeklyHours} suffix=" hrs" />
              <SliderInput id="hourly-rate-slider" label="Average hourly rate" value={hourlyRate} min={20} max={150} step={5} onChange={setHourlyRate} prefix="$" suffix="/hr" />

              <div className="rounded-lg p-4 mt-4" style={{
                background: 'var(--accent-glow)',
                border: '1px solid var(--accent-line)',
              }}>
                <span className="text-sm" style={{ color: 'var(--white-dim)' }}>
                  We typically automate{' '}
                  <span className="font-bold" style={{ color: 'var(--accent)' }}>60%</span>
                  {' '}of repetitive work within 90 days.
                </span>
              </div>
            </div>

            {/* Results */}
            <div className="flex flex-col gap-4">
              <div className="rounded-xl p-6 text-center" style={{
                background: 'var(--accent-glow)',
                border: '1px solid var(--accent)',
              }}>
                <div className="text-sm mb-1" style={{ color: 'var(--white-dim)' }}>Annual value recovered with AI automation</div>
                <div className="font-black leading-none mb-1" style={{
                  fontFamily: 'Barlow Condensed, sans-serif',
                  fontSize: 48,
                  color: 'var(--accent)',
                }} suppressHydrationWarning>{fmt(annualValueRecovered)}</div>
                <div className="text-xs" style={{ color: 'var(--white-dim)' }}>per year</div>
              </div>

              {[
                {
                  label: 'Current annual cost of repetitive tasks',
                  value: fmt(annualCostRepetitive),
                  sub: `${teamSize} people × ${weeklyHours} hrs/wk × ${fmt(hourlyRate)}/hr`,
                  accent: false,
                },
                {
                  label: 'Hours freed per year',
                  value: Math.round(hoursSaved).toLocaleString(LOCALE) + ' hrs',
                  sub: '60% automation rate across your team',
                  accent: false,
                },
                {
                  label: 'Value of time recovered',
                  value: fmt(annualValueRecovered),
                  sub: 'Hours freed × your hourly rate',
                  accent: true,
                },
              ].map((s, i) => (
                <div key={i} className="rounded-xl px-5 py-4 flex justify-between items-center" style={{
                  background: s.accent ? 'var(--accent-glow)' : 'var(--bg-2)',
                  border: `1px solid ${s.accent ? 'var(--accent-line)' : 'rgba(238,240,245,0.07)'}`,
                }}>
                  <div>
                    <div className="text-sm" style={{ color: 'var(--white-dim)' }}>{s.label}</div>
                    <div className="text-xs mt-0.5" style={{ color: 'rgba(238,240,245,0.3)' }} suppressHydrationWarning>{s.sub}</div>
                  </div>
                  <div className="font-black text-2xl ml-4 whitespace-nowrap" style={{
                    fontFamily: 'Barlow Condensed, sans-serif',
                    color: s.accent ? 'var(--accent)' : 'var(--white)',
                  }} suppressHydrationWarning>{s.value}</div>
                </div>
              ))}

              <div className="text-center mt-2">
                <a href="/contact"
                  className="inline-block px-8 py-3.5 rounded-lg font-bold uppercase tracking-wide text-sm text-white"
                  style={{
                    background: 'var(--accent)',
                    fontFamily: 'Barlow Condensed, sans-serif',
                    boxShadow: '0 4px 24px var(--accent-glow)',
                    textDecoration: 'none',
                  }}>
                  See What We Can Automate →
                </a>
              </div>
            </div>
          </div>
        )}
        </div>
      </div>
    </section>
  )
}
